import React, { useRef, useEffect, useState, useMemo } from 'react';
import { useSolplace } from '../context/SolplaceContext';
import './CanvasContainer.css';

const CanvasContainer = () => {
  const canvasRef = useRef(null);
  const { 
    wallet, 
    canvasData, 
    selectedColor, 
    zoom, 
    selectedPixel,
    placePixel,
    dispatch,
    loading
  } = useSolplace();
  
  const [coordinates, setCoordinates] = useState({ x: 0, y: 0 });
  const [pixelInfo, setPixelInfo] = useState(null);
  const [showPixelInfo, setShowPixelInfo] = useState(false);
  const pixelSize = 4;
  // External map (Yandex static) + local fallback
  const yandexMapUrl = 'https://static-maps.yandex.ru/1.x/?lang=en_US&ll=104.1954,35.8617&z=4&size=800,600&l=map';
  const mapBg = `url("${yandexMapUrl}"), url("${process.env.PUBLIC_URL}/china-map.svg")`;
  const userList = useMemo(() => {
    const owners = new Map();
    canvasData.forEach((pixel) => {
      if (pixel.owner) {
        owners.set(pixel.owner, (owners.get(pixel.owner) || 0) + 1);
      }
    });
    return Array.from(owners.entries())
      .map(([owner, count]) => ({ owner, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 100);
  }, [canvasData]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    setupCanvas(canvas, ctx);
    redrawCanvas(canvas, ctx);
  }, [canvasData]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const wheelListener = (e) => {
      e.preventDefault();
      handleWheel(e);
    };

    canvas.addEventListener('wheel', wheelListener, { passive: false });
    return () => canvas.removeEventListener('wheel', wheelListener);
  }, [zoom]);

  const setupCanvas = (canvas, ctx) => {
    ctx.imageSmoothingEnabled = false;
    ctx.fillStyle = 'rgba(255, 255, 255, 0.02)';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Draw subtle grid
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.05)';
    ctx.lineWidth = 0.5;
    for (let x = 0; x < canvas.width; x += pixelSize) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, canvas.height);
      ctx.stroke();
    }
    for (let y = 0; y < canvas.height; y += pixelSize) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(canvas.width, y);
      ctx.stroke();
    }
    
    // Draw map outline/border
    ctx.strokeStyle = '#00ff88';
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.rect(0, 0, canvas.width, canvas.height);
    ctx.stroke();
    
    // Inner glow effect
    ctx.strokeStyle = 'rgba(0, 255, 136, 0.3)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.rect(1, 1, canvas.width - 2, canvas.height - 2);
    ctx.stroke();
  };

  const redrawCanvas = (canvas, ctx) => {
    setupCanvas(canvas, ctx);
    
    canvasData.forEach((pixel, key) => {
      const [x, y] = key.split(',').map(Number);
      ctx.fillStyle = pixel.color;
      ctx.fillRect(x * pixelSize, y * pixelSize, pixelSize, pixelSize);
    });
  };

  const handleMouseMove = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const x = Math.floor((e.clientX - rect.left) / (zoom * pixelSize));
    const y = Math.floor((e.clientY - rect.top) / (zoom * pixelSize));
    
    setCoordinates({ x, y });
    dispatch({ type: 'SET_SELECTED_PIXEL', payload: { x, y } });

    // Show pixel info if there's a pixel at this position
    const pixelKey = `${x},${y}`;
    const pixel = canvasData.get(pixelKey);

    if (pixel) {
      setPixelInfo(pixel);
      setShowPixelInfo(true);
    } else {
      setShowPixelInfo(false);
    }
  };

  const handleClick = async (e) => {
    if (!wallet) {
      return;
    }

    const rect = canvasRef.current.getBoundingClientRect();
    const x = Math.floor((e.clientX - rect.left) / (zoom * pixelSize));
    const y = Math.floor((e.clientY - rect.top) / (zoom * pixelSize));

    if (await placePixel(x, y)) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = selectedColor;
      ctx.fillRect(x * pixelSize, y * pixelSize, pixelSize, pixelSize);
    }
  };

  const handleWheel = (e) => {
    e.preventDefault();
    const zoomFactor = e.deltaY > 0 ? 0.9 : 1.1;
    const newZoom = Math.max(0.5, Math.min(5, zoom * zoomFactor));
    dispatch({ type: 'SET_ZOOM', payload: newZoom });
  };

  const zoomCanvas = (factor) => {
    const newZoom = Math.max(0.5, Math.min(5, zoom * factor));
    dispatch({ type: 'SET_ZOOM', payload: newZoom });
  };

  const resetZoom = () => {
    dispatch({ type: 'SET_ZOOM', payload: 1 });
  };

  const formatAddress = (address) => {
    return address.substring(0, 6) + '...' + address.substring(38);
  };

  return (
    <div className="canvas-container">
      {loading && (
        <div className="loading">
          <div className="spinner"></div>
          Loading canvas...
        </div>
      )}
      
      <div className="canvas-layout">
        <div className="canvas-wrapper">
          <div className="zug-overlay"></div>
          <div className="crypto-badge">Crypto Canvas</div>
          
          <canvas
            ref={canvasRef}
            width="800"
            height="600"
            className="canvas"
            style={{
              transform: `scale(${zoom})`,
              transformOrigin: 'top left'
            }}
            onMouseMove={handleMouseMove}
            onClick={handleClick}
          />
          <div className="map-outline" style={{ '--map-bg': mapBg }}></div>
          
          <div className="location-label">CHINA</div>
          
          <div className="zoom-controls">
            <button 
              className="zoom-btn" 
              onClick={() => zoomCanvas(1.2)}
              title="Zoom In"
            >
              +
            </button>
            <button 
              className="zoom-btn" 
              onClick={() => zoomCanvas(0.8)}
              title="Zoom Out"
            >
              -
            </button>
            <button 
              className="zoom-btn" 
              onClick={resetZoom}
              title="Reset Zoom"
            >
              ⌂
            </button>
          </div>
        </div>

        <aside className="canvas-sidebar">
          <div className="sidebar-header">
            <div className="sidebar-title">Active users</div>
            <div className="sidebar-count">{userList.length}</div>
          </div>
          <div className="sidebar-list">
            {userList.length === 0 ? (
              <div className="sidebar-empty">No users yet</div>
            ) : (
              userList.map((item, idx) => (
                <div className="sidebar-item" key={item.owner}>
                  <div className="sidebar-rank">#{idx + 1}</div>
                  <div className="sidebar-owner">{formatAddress(item.owner)}</div>
                  <div className="sidebar-pixels">{item.count} px</div>
                </div>
              ))
            )}
          </div>
        </aside>
      </div>
      
      <div className="coordinates">
        X: {coordinates.x}, Y: {coordinates.y}
      </div>
      
      {showPixelInfo && pixelInfo && (
        <div className="pixel-info">
          <div><strong>Pixel Info:</strong></div>
          <div>Owner: {formatAddress(pixelInfo.owner)}</div>
          <div>Color: {pixelInfo.color}</div>
          <div>Placed: {new Date(pixelInfo.timestamp).toLocaleString()}</div>
        </div>
      )}
    </div>
  );
};

export default CanvasContainer;
